import { ReactNode, useCallback, useState, useMemo } from 'react';

import { loaders, FullProject, FullTask, User, isProjectFullBlocked } from 'entities';
import { useRouteParams } from 'routes';

import { TaskModal as TaskModalComponent } from '../TaskModal';

import { useRecentProjectSetter, useSingleProject } from 'providers/Project';
import { useRealTimeSubscription } from 'providers/RealTime';
import { useUser } from 'hooks/user';

import { useUrlStateV2, usePageTitle } from 'hooks';
import { getProjectActiveTabs, projectTabs, Tab } from '../tabs';
import { listGet } from 'utils/array';

import { ProjectModal, ProjectProfileContext, projectProfileContext, TaskModal } from './context';

export function ProjectProfileProvider({ children }: { children: ReactNode }) {
  const userData = useUser();

  const { projectId } = useRouteParams('projectProfile');

  const { project, updateProject } = useSingleProject(projectId);

  const { saveRecentProject } = useRecentProjectSetter();

  const [queryParams, setQueryParams] = useUrlStateV2({
    tab: {
      defaultValue: (userData.team.isInvoiceTeam ? 'invoice' : 'execution') as Tab,
      validate: (v) => Object.keys(projectTabs).includes(v),
    },

    taskId: '',
    issueId: '',
    recruitmentId: '',
  });

  const { loading, error, retry } = loaders.projects.fullProject.use({
    blockLoad: !projectId,

    params: {
      id: projectId,
    },

    refreshMinutes: 3,

    onDataChange: (data) => {
      saveRecentProject(data.id);

      updateProject(data.id, (old) => ({
        ...old,
        ...data,
        tasks: data.tasks.map((t) => ({ ...listGet(old?.tasks || [], t.id), ...t })),
      }));

      const { tab: queryTab } = queryParams;

      setQueryParams({
        tab:
          (listGet(
            getProjectActiveTabs(data, userData),
            queryTab || (userData.team.isInvoiceTeam ? 'invoice' : 'execution'),
          )?.value as Tab) ?? queryParams.tab,
      });
    },
  });

  const recruitments = loaders.projects.listProjectRecruitments.use({
    defaultValue: [],

    params: { projectId },

    reloadOnChangeRef: project?.activeRecruitments,

    onDataChange: (recruits) => {
      if (!queryParams.recruitmentId) return;

      const queryRecruitment = recruits.find((r) => r.id === queryParams.recruitmentId);

      if (!queryRecruitment) return setQueryParams({ recruitmentId: null });
    },
  });

  usePageTitle(project?.name);

  const [openedModal, setOpenedModal] = useState<ProjectModal | null>(null);

  const [extraTaskModal, setExtraTaskModal] = useState<{ modal: TaskModal | null; id: string }>({
    modal: null,
    id: '',
  });

  const projectUsers = useMemo(() => {
    if (!project) return {};

    const users = [
      project.owner,
      ...recruitments.data.map((r) => r.owner),
      ...(project.tasks || [])
        .map(({ owner, subOwner, subTasks = [] }) =>
          [owner, subOwner, ...subTasks.map((sub) => sub.owner)].filter(Boolean),
        )
        .flat(),
    ] as User[];

    return Object.fromEntries(users.map((user) => [user.id, user]));
  }, [project, recruitments.data]);

  const updateThisProject = useCallback(
    (newData: FullProject | ((old: FullProject) => FullProject)) => {
      updateProject(projectId, newData);
    },
    [updateProject, projectId],
  );

  const updateTask: ProjectProfileContext['updateTask'] = useCallback(
    (id, taskUpdate) => {
      const updater =
        typeof taskUpdate === 'function' ? taskUpdate : (t: FullTask) => ({ ...t, ...taskUpdate });

      updateThisProject((old) => ({
        ...old,
        tasks: old.tasks.map((t) => (t.id === id ? updater(t as FullTask) : t)),
      }));
    },
    [updateThisProject],
  );

  const openExtraTaskModal = useCallback((modal: TaskModal, id: string) => {
    setExtraTaskModal({ modal, id });
  }, []);

  const closeExtraTaskModal = useCallback(() => {
    setExtraTaskModal({
      modal: null,
      id: '',
    });
  }, []);

  useRealTimeSubscription('INVOICE_CONTROL_ASSIGNMENT', ({ project }) => {
    if (project.id === projectId) retry();
  });

  const contextData = useMemo(
    () => ({
      project,

      tab: queryParams.tab,
      setTab: (newTab: Tab) => setQueryParams({ tab: newTab }),

      isOwner: userData.id === project?.owner.id,

      projectUsers,

      fullBlocked: isProjectFullBlocked(project?.status || '9'),

      projectModalConfig: {
        modal: openedModal,
        openModal: (modal: ProjectModal) => setOpenedModal(modal),
        closeModal: () => setOpenedModal(null),
      },

      taskModalConfig: {
        openModal: openExtraTaskModal,
        closeModal: closeExtraTaskModal,
        ...extraTaskModal,
        data: project?.tasks.find((task) => task.id == extraTaskModal.id) as FullTask,
      },

      openTask: (taskId: string) => setQueryParams({ taskId }),

      loading: loading || error,

      refreshProject: retry,
      updateProject: updateThisProject,
      updateTask,

      id: projectId,

      queryParams,
      setQueryParams,

      recruitments,
    }),
    [
      openedModal,
      setQueryParams,
      updateTask,
      project,
      projectUsers,
      loading,
      error,
      openExtraTaskModal,
      closeExtraTaskModal,
      retry,
      updateThisProject,
      extraTaskModal,
      userData.id,
      projectId,
      queryParams,
      recruitments,
    ],
  );

  return (
    <projectProfileContext.Provider value={contextData}>
      {!loading && queryParams.taskId && (
        <TaskModalComponent
          taskId={queryParams.taskId}
          close={() => setQueryParams({ taskId: null })}
        />
      )}

      <>{children}</>
    </projectProfileContext.Provider>
  );
}
